import isEmpty from 'lodash/isEmpty';

import Constants from '~/config/Constants';
import FeatureFlagNames from '~/config/Constants/FeatureFlags';
import $featureFlags from '~/services/FeatureFlagsService';
import { getQuizCreatedById } from '~/utils/QuestionUtils';
import type UserService from '~/services/UserService';

type RawUser = Partial<UserService>;

export function isCorporate(args: { occupation: string }) {
  const { occupation } = args;
  return (
    !isEmpty(occupation)
    && (
      occupation === Constants.UserOccupation.CORPORATE
      || occupation === Constants.UserOccupation.CORPORATE_TEACHER
      || occupation === Constants.UserOccupation.CORPORATE_LEARNER
    )
  );
}

export function getUserName(user: RawUser) {
  if (!user) {
    return '';
  }

  const {
    title,
    firstName,
    lastName,
    local: {
      email,
      userName,
    } = {},
  } = user;

  if (!firstName && !lastName) {
    return (email || userName || '').trim();
  }

  if ($featureFlags().isEnabled(FeatureFlagNames.BTS_FC)) {
    return `${title?.concat(' ') ?? ''}${firstName?.concat(' ') ?? ''}`.trim();
  }

  return `${title?.concat(' ') ?? ''}${firstName?.concat(' ') ?? ''}${lastName ?? ''}`.trim();
}

export function computeUserAccessLevel(user: RawUser) {
  // TODO
  // figure out the user's access level
  // Possible values -
  // 1. free tier
  // 2. paid-teacher tier
  // 3. paid-district tier

  if (!user) {
    return Constants.UserTypes.UNREGISTERED;
  }

  if (user.paidOrganization) {
    return Constants.UserTypes.SND;
  } if (user.isSuper) {
    return Constants.UserTypes.PAID;
  }
  return Constants.UserTypes.BASIC;
}

export function getAccountType(user: UserService) {
  const planName = user.plan;

  if (!user.id) {
    return Constants.AccountTypes.ANONYMOUS;
  }

  if (user.isPartOfAnOrganization && planName !== '') {
    if (!user.hasActiveSnDPlan && user.isSuperOrHasNonQfwSubscription) {
      return Constants.AccountTypes.SUPER;
    }
    return Constants.AccountTypes.SCHOOL_AND_DISTRICT;
  }

  if (!planName) {
    if (isCorporate(user)) {
      return Constants.AccountTypes.CORPORATE_INTRO;
    }

    return Constants.AccountTypes.BASIC;
  }

  if (user.isSuperOrHasNonQfwSubscription) {
    return Constants.AccountTypes.SUPER;
  }

  // If none of the above, it must be a corporate account
  switch (planName) {
    case 'Intro':
      return Constants.AccountTypes.CORPORATE_INTRO;
    case 'Starter':
      return Constants.AccountTypes.CORPORATE_STARTER;
    case 'Standard':
      return Constants.AccountTypes.CORPORATE_STANDARD;
    case 'Standard (Non Profit)':
      return Constants.AccountTypes.CORPORATE_STANDARD_NONPROFIT;
    case 'Premier':
      return Constants.AccountTypes.CORPORATE_PREMIER;
    case 'Enterprise':
      return Constants.AccountTypes.ENTERPRISE;
    case 'Essential':
      return Constants.AccountTypes.ESSENTIAL;
    case 'Event':
      return Constants.AccountTypes.EVENT;
    default:
      return Constants.AccountTypes.SUPER;
  }
}

export function getOrgIdsFromUser(user: RawUser) {
  const paidOrgIds = (user.paidOrganizations ?? []).map((org: { organizationId: string }) => org.organizationId);
  const orgIds = (user.organizationIds ?? []).map((orgId: string) => orgId);
  return Array.from(new Set([...paidOrgIds, ...orgIds]));
}

export function isUserFromPaidOrg(user: RawUser) {
  return computeUserAccessLevel(user) === Constants.UserTypes.SND;
}

export function getUsersPaidOrgIds(user: RawUser) {
  if (!isUserFromPaidOrg(user)) {
    return [];
  }
  if (user.paidOrganizations?.length) {
    return user.paidOrganizations?.map((org: { id: string }) => org.id) ?? [];
  }
  if (user.paidOrganization) { return [user.paidOrganization]; }
  return [];
}

export function isUserQuizOwner(quiz: any, user: RawUser) {
  if (!user?.isLoggedIn || isEmpty(quiz?.createdBy?.id)) {
    return false;
  }
  return user?.id === getQuizCreatedById(quiz.createdBy);
}

interface UserAvatarConfigItem {
  url: string,
  subtype?: 'head' | 'body',
  value?: string,
}
export type ItemCategory = 'free' | 'common' | 'rare' | 'legendary';
type AssetCategory = 'footwear' | 'bottomwear' | 'topwear' | 'accessory' | 'headgear' | 'backpack';
interface InventoryItem {
  url: string,
  _id: string,
  enabled: boolean,
  type: AssetCategory,
  name: string,
  price?: number,
  category: ItemCategory,
  subtype?: 'head' | 'body',
  value?: string,
}
type UserAvatarConfig = Record<AssetCategory, UserAvatarConfigItem>;

export function transformServerAvatarToClient(input: Record<AssetCategory, InventoryItem>): UserAvatarConfig | undefined {
  if (!input) {
    return;
  }
  const newConfig: UserAvatarConfig = Object.entries(input).reduce((acc, [key, val]) => {
    acc[key as AssetCategory] = {
      url: val.url || `https://quizizz.com/_media/avatar-assets/${val.type}/${val.name}.svg`,
      value: val.value,
    };

    if (val.subtype) {
      acc[key as AssetCategory].subtype = val.subtype;
    }

    return acc;
  }, {} as UserAvatarConfig);
  return newConfig;
}
